import api from "./api";
import { parsePaginatedList } from "../utils/pagination";

export interface Transaction {
  id: string;
  cardHolderId: string;
  branchId: string;
  salesPointId?: string;
  saleId?: string;
  amount: number;
  type: "debit" | "credit";
  balanceBefore: number;
  balanceAfter: number;
  description?: string;
  transactionDate: string;
  createdAt: string;
  createdBy: string;
  cardHolder?: {
    id: string;
    holderName: string;
    phone?: string;
    email?: string;
    isActive?: boolean;
  };
  branch?: {
    id: string;
    name: string;
    address?: string;
  };
  salesPoint?: {
    id: string;
    name: string;
  } | null;
  creator?: {
    id: string;
    name: string;
    email?: string;
  } | null;
}

export type TransactionSortField =
  | "holderName"
  | "type"
  | "amount"
  | "balanceBefore"
  | "balanceAfter"
  | "description"
  | "transactionDate"
  | "branch";

export type TransactionSortOrder = "ASC" | "DESC";

export interface TransactionFilter {
  pageNo?: number;
  pageSize?: number;
  cardHolderId?: string;
  branchId?: string;
  salesPointId?: string;
  type?: "debit" | "credit";
  search?: string;
  startDate?: string;
  endDate?: string;
  sortBy?: TransactionSortField;
  sortOrder?: TransactionSortOrder;
}

export interface CreateTransactionData {
  cardHolderId: string;
  amount: number;
  type: "debit" | "credit";
  description?: string;
}

export interface TransactionsListResult {
  transactions: Transaction[];
  total: number;
  creditCount: number;
  debitCount: number;
  totalCredit: number;
  totalDebit: number;
}

export const transactionAPI = {
  getTransactions: async (
    filters?: TransactionFilter
  ): Promise<TransactionsListResult> => {
    const params = new URLSearchParams();

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, String(value));
        }
      });
    }

    const response = await api.get(`/transactions?${params.toString()}`);
    const responseData = response.data.data;
    const { data, total } = parsePaginatedList<Transaction>(responseData);
    const summary = responseData.summary ?? {};

    return {
      transactions: data,
      total,
      creditCount: Number(summary.creditCount ?? 0),
      debitCount: Number(summary.debitCount ?? 0),
      totalCredit: Number(summary.totalCredit ?? 0),
      totalDebit: Number(summary.totalDebit ?? 0),
    };
  },

  getTransactionById: async (id: string): Promise<Transaction> => {
    const response = await api.get(`/transactions/${id}`);
    return response.data.data;
  },

  createTransaction: async (data: CreateTransactionData) => {
    const response = await api.post("/transactions", data);
    return response.data.data;
  },

  getTransactionStats: async (
    period: "daily" | "weekly" | "monthly" = "daily"
  ) => {
    const response = await api.get(`/transactions/stats?period=${period}`);
    return response.data.data;
  },
};
